import * as api from '@/api';
import { getPoiParams, /* getCommonBoundingBox, getCurrentBoundingBox, getCurrentRouteCorridor */ } from '@/utils/poi-params';
import { getPOIResultByType, getActiveSubApiTypes, getSubApiTypes } from '@/utils/poi-utils'
import { getPoiConfig } from '@/utils/poi-data/poi-config'
import { loadMorePOIResultsWithPagination } from '@/utils/poi-api-utils'
import eventBus from '@/utils/event-bus';

const MULTI_POI_DEFAULT_SKIPPED = ['partners'];
let responseCount = {};
let totalResponseCount = {};

export async function loadPOIResults({getters, dispatch, commit}) {
    try {
        if(getters.selectedPOIs.length) {
            commit('setPoiLoading', true);
        }
        let promises = getters.selectedPOIs.map(poi => {
            return dispatch('loadPOIResultsByType', { type: poi });
        });
        await Promise.all(promises);
    } catch(error) {
        console.error(error)
    } finally {
        if(getters.selectedPOIs.length) {
            commit('setPoiLoading', false);
        }
    }
}


export async function loadPOIResultsByType({commit, dispatch, getters}, {type, skipTrafficSelection}) {
    return new Promise((resolve) => {
        const poiConfig = getPoiConfig(type)
        const apiType = poiConfig.apiType;
        const params = getPoiParams(type);
        let subApiTypes = getActiveSubApiTypes(poiConfig, params);
        responseCount[type] = 0;
        totalResponseCount[type] = 2;

        if(subApiTypes) {
            totalResponseCount[type] += subApiTypes.length;
        }

        if(!params) {
            console.error('No settings specified for that POI type. Call canceled.');
            return false;
        }
        if(!params.geographic) {
            console.error('Missing geographic data. Call canceled.');
            return false;
        }
        commit('setLoadingPOIType', {type, loading: true});
        commit('setPoiApiErrorsByType', {type, data: false})

        // call for detailed list items
        api.poi.getPOIsForListByType(apiType, {...params}) 
            .then(response => {
                responseCount[type]++;
                const data = getPOIResultByType(response, type);
                commit('setPoiResultsByType', {type, data});
                commit('selectPOI', type);
                dispatch('updateMapTraffic');

                if(type === 'traffic' && !skipTrafficSelection) {
                    commit('setTrafficPOIselected', true);
                }

                if(response.data && response.data.header && response.data.header.count !== undefined) {
                    commit('setPOIPaginationDataByType', {
                        type,
                        more: response.data.header.count < response.data.header.totalCount,
                        userCoordinates: getters.userCoordinates ? getters.userCoordinates : false,
                        apiType,
                        params
                    })
                }
                maybeResolvePOICall(responseCount[type], type, resolve, commit);
            })
            .catch((error) => {
                console.error(error);
                responseCount[type]++;
                commit('setPoiResultsByType', {type, data: []});
                commit('selectPOI', type);
                dispatch('updateMapTraffic');
                if(type === 'traffic' && !skipTrafficSelection) {
                    commit('setTrafficPOIselected', true);
                }
                if(error.response) {
                    commit('setPoiApiErrorsByType', {type, data: error.response.data.errors})
                }
                maybeResolvePOICall(responseCount[type], type, resolve, commit);
            })

        // call for map items
        // skip certain api types for map
        if(!MULTI_POI_DEFAULT_SKIPPED.includes(type)) {
            eventBus.$emit('removePoisFromMap', {type, isSubApi: false})
            getAndShowMapPOIByType(apiType, params, type, type, resolve, commit);
        } else {
            totalResponseCount[type]--
        }

        // call for additional map items (from sub APIs, because for the map we can't use the mulit-poi requests...)
        if(subApiTypes) {
            subApiTypes.forEach(subApiType => {
                const subApiParams = getPoiParams(type, subApiType);
                eventBus.$emit('removePoisFromMap', {type: subApiType, isSubApi: true})      
                getAndShowMapPOIByType(subApiType, subApiParams, subApiType, type, resolve, commit, true);
            })
        }

    })
}

function getAndShowMapPOIByType(apiType, params, poiType, poiSelectorType, resolve, commit, isSubApi = false, start = 0) {
    api.poi.getPOIsForMapByType(apiType, params, start) 
    .then(async (response) => {
        let data = getPOIResultByType(response, poiType);

        const moreData = await loadMorePOIResultsWithPagination(apiType, params, poiType, response)
        if(data && moreData) { // if moreData is false, request was cancelled or error -> draw no pins
            data = [...data, ...moreData]
            eventBus.$emit('showPoisOnMap', {type: poiType, data, isSubApi})
        }
        responseCount[poiSelectorType]++;
        maybeResolvePOICall(responseCount[poiSelectorType], poiSelectorType, resolve, commit);
    })
    .catch((error) => {
        console.error(error);
        responseCount[poiSelectorType]++;
        maybeResolvePOICall(responseCount[poiSelectorType], poiSelectorType, resolve, commit);
    })
}

function maybeResolvePOICall(responseCount, type, resolve, commit) {
    // wait for both list and map calls to finish
    if(responseCount == totalResponseCount[type]) {
        commit('setLoadingPOIType', {type, loading: false});
        resolve()
    }
}

export async function loadMorePOIResults({getters, dispatch}, offsets) {
    try {
        let promises = getters.selectedPOIs.map(poi => {
            return dispatch('loadMorePOIResultsByType', {type: poi, offset: offsets[poi]});
        });
        await Promise.all(promises);
    } catch(error) {
        console.error(error)
    }
}

export async function loadMorePOIResultsByType({commit, getters}, {type, offset}) {
    return new Promise((resolve) => {
        if(!getters.poiPaginationData[type]) {
            resolve()
            return false;
        }

        // make a shallow copy of the stored value to not overwrite the geographic param with the merged bounding box
        const typePaginationData = JSON.parse ( JSON.stringify ( getters.poiPaginationData[type] ) )
        if(!typePaginationData.more) {
            resolve()
            return false;
        }
        commit('setLoadingPOIType', {type, loading: true});

        // get only a currently visible part the stored mapBounds so no unnecesary data is loaded when the user moved / zoomed the map after the first poi search (as these results will be filtered out on the frontend anyways)
        // const storedBoundingBox = typePaginationData.params.geographic.boundingBox;
        // const corridor = getters.routeCorridor;
        // if(corridor) {
        //     typePaginationData.params.geographic.corridor = getCurrentRouteCorridor(corridor)
        // } 
        // if(storedBoundingBox) {
        //     typePaginationData.params.geographic.boundingBox = getCommonBoundingBox(storedBoundingBox);
        // } else {
        //     typePaginationData.params.geographic.boundingBox = getCurrentBoundingBox();
        // }

        // temporary, just a dev helper, todo: remove it
        // if(typePaginationData.params.geographic.boundingBox) {
        //     eventBus.$emit('drawBoundingBox', typePaginationData.params.geographic.boundingBox);
        // }

        // call for detailed list items
        api.poi.getPOIsForListByType(typePaginationData.apiType, {...typePaginationData.params}, typePaginationData, offset) 
            .then(response => {
                const data = response.data.data ? response.data.data : response.data.results;
                if(data) {
                    commit('appendPoiResultsByType', {type, data});
                }
                if(response.data && response.data.header && response.data.header.count) {
                    if(response.data.header.count + offset >= response.data.header.totalCount) {
                        commit('setPoiPaginationMore', {type, more: false});
                    }
                }
                commit('setLoadingPOIType', {type, loading: false});
                resolve()
            })
            .catch((error) => {
                console.error(error);
                commit('setLoadingPOIType', {type, loading: false});
                resolve()
            })

    })
}

export function resetPOIResults({commit, getters}) {
    getters.selectedPOIs.forEach(poi => {
        commit('removePOIResultType', poi);
    })
    commit('resetPoiPagination');
}

export function deselectAllPOIs({commit, dispatch, getters}) {
    dispatch('resetPOIResults');
    commit('deselectAllPOIs');
    commit('setTrafficPOIselected', false);
    commit('setMapTraffic', false);
    // cancel the loading POIs
    if(getters.loadingPOIs && getters.loadingPOIs.length > 0) {
        getters.loadingPOIs.forEach(poi => {
            dispatch('cancelPOILoading', poi);
        })
    }
}

export function deselectPOI({commit, dispatch}, poi) {
    commit('deselectPOI', poi);
    commit('removePOIResultType', poi);
    dispatch('updateMapTraffic');

    if(poi === 'traffic') {
        commit('setTrafficPOIselected', false);
    }
}
export function cancelPOILoading({commit}, poi) {
    const apiType = getPoiConfig(poi).apiType;
    api.poi.cancelPOIrequest(apiType);
    // cancel subapis too
    const subApis = getSubApiTypes(poi)
    if(subApis && subApis.length > 0) {
        subApis.forEach(subApi => {
            api.poi.cancelPOIrequest(subApi);
        })
    }
    commit('setLoadingPOIType', {type: poi, loading: false}); 
}


export function updateMapTraffic({getters, commit}) {
    if(getters.selectedPOIs.includes('traffic')) {
        if(getters.fullPoiSettings.traffic.type.includes('Verkehrsfluss') && !getters.fullPoiSettings.traffic.other.favouritesOnly && getters.routeTrafficTime.isNow) {
            if(!getters.mapTraffic) {
                commit('setMapTraffic', true);
            }
        } else {
            if(getters.mapTraffic) {
                commit('setMapTraffic', false);
            }
        }
    } else {
        if(getters.mapTraffic) {
            commit('setMapTraffic', false);
        }
    }
}

export async function getMyOeamtcBase({commit, getters}) {
    try {
        const response = await api.myGeo.getOeamtcBase(getters.user.identityKey);
        console.log({response});
        let value = response.data.id ? response.data.id : null;
        commit('setMyOeamtcBase', value);
    } catch (e) {
        console.error(e);
    }
}

export async function addMyOeamtcBase({commit}, id) {
    try {
        const response = await api.myGeo.addOeamtcBase(id);
        console.log({response});
        commit('setMyOeamtcBase', id);
    } catch (e) {
        console.error(e);
    }
}

export async function removeMyOeamtcBase({commit, getters}) {
    try {
        const response = await api.myGeo.removeOeamtcBase(getters.user.identityKey);
        console.log({response});
        commit('setMyOeamtcBase', null);
    } catch (e) {
        console.error(e);
    }
}

export async function loadGasStationsOperatorList({commit}) {
    try {
        const response = await api.poi.getGasStationsOperatorList()
        const stationGroups = response.data.computed.stationGroups?.stationGroups
        if(stationGroups) {
            commit('setDynamicPoiElements', { type: 'gasStations', data: {
                categories: [
                    {
                        children: stationGroups.map(item => {
                            return {
                                value: item.id,
                                label: item.name,
                            }
                        })
                    }
                ]
            } })
        }
    } catch (e) {
        console.error(e);
    }
}

